import { useMemo, useState } from 'react';
import { GROUP_LABEL } from '../lib/rules.js';
import { summarize } from '../lib/summary.js';
import { buildResultCard } from '../lib/resultCard.js';
import { buildNarrative } from '../lib/narrative.js';
import { pickPrevious } from '../lib/compareCards.js';
import LifeDrama from '../components/LifeDrama.jsx';
import MoneyGraph from '../components/MoneyGraph.jsx';
import { LIFE_RULES } from '../lib/life.js';
import * as E from '../lib/engine.js';
import { buildLifeTimeline, buildMoneyFlow, lifeBasis } from '../lib/lifeResult.js';
import { buildKeyTiles } from '../lib/lifeResultSections.js';
import './ResultPage.css';
import { WindowTitle } from '../components/Desktop.jsx';

const STEM_KO = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const BRANCH_KO = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
const ANIMAL_KO = ['쥐', '소', '호랑이', '토끼', '용', '뱀', '말', '양', '원숭이', '닭', '개', '돼지'];
const STEM_COLOR = ['초록', '초록', '빨강', '빨강', '노랑', '노랑', '흰색', '흰색', '검정', '검정'];
const value = (n) => Number.isFinite(n) ? Math.round(n * 10) / 10 : '미기록';

const xml = (text) => String(text).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[char]));
const wrap = (text, length = 36) => {
  const chars = Array.from(String(text)), lines = [];
  for (let start = 0; start < chars.length; start += length) lines.push(chars.slice(start, start + length).join(''));
  return lines;
};

const TURNING_LABEL = ['가장 크게 오른 선택', '가장 크게 내려간 사건', '가장 큰 전환'];

// 결과 한 장 카드와 같은 내용(제목, 숫자 3개, 전환점, 고정 문장)을 담은 공유 SVG
function shareSvg(run, card, includeBirth, narrative = null) {
  const day = `${STEM_KO[run.saju.day.stem]}${BRANCH_KO[run.saju.day.branch]}일 · ${STEM_COLOR[run.saju.day.stem]} ${ANIMAL_KO[run.saju.day.branch]}`;
  const sub = [`${card.endAge}세까지의 기록`, day, ...(includeBirth ? [`출생 ${run.saju.solarDate}${run.birth?.hour == null ? ' · 시각 모름' : ''}`] : [])];
  const points = card.turningPoints.map((point, index) => point
    ? { label: TURNING_LABEL[index], lines: wrap(`${point.age}세 · ${point.title}`, 34).slice(0, 2), detail: point.detail }
    : { label: TURNING_LABEL[index], lines: ['기록 없음'], detail: '' });
  let y = 150;
  const parts = [];
  parts.push(`<text x="70" y="${y}" font-size="38" font-weight="700">${xml(card.title)}</text>`);
  sub.forEach((line, index) => parts.push(`<text x="70" y="${y + 40 + index * 28}" font-size="18" fill="#5c5a54">${xml(line)}</text>`));
  y += 40 + sub.length * 28 + 20;
  const story = wrap(narrative?.text ?? '', 34);
  const storyShown = story.slice(0, 6);
  if (story.length > 6) storyShown[5] = `${storyShown[5].slice(0, -1)}…`;
  storyShown.forEach((line, index) => parts.push(`<text x="70" y="${y + index * 26}" font-size="16">${xml(line)}</text>`));
  y += storyShown.length ? storyShown.length * 26 + 24 : 10;
  card.numbers.forEach((number, index) => {
    const x = 70 + index * 224;
    parts.push(`<text x="${x}" y="${y + 52}" font-size="56" font-weight="700">${number.value}</text><text x="${x}" y="${y + 86}" font-size="19" fill="#5c5a54">${xml(number.label)}</text>`);
  });
  y += 130;
  points.forEach((point) => {
    parts.push(`<text x="70" y="${y}" font-size="15" fill="#5c5a54">${xml(point.label)}${point.detail ? ` · ${xml(point.detail)}` : ''}</text>`);
    point.lines.forEach((line, index) => parts.push(`<text x="70" y="${y + 26 + index * 26}" font-size="18">${xml(line)}</text>`));
    y += 36 + point.lines.length * 26 + 14;
  });
  const bottom = y + 20;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="${bottom + 110}" viewBox="0 0 800 ${bottom + 110}">
    <rect width="800" height="100%" fill="#f3f2ef"/><rect x="32" y="32" width="736" height="${bottom + 46}" rx="22" fill="#fff" stroke="#c9c6bf"/>
    <g font-family="'Noto Sans KR',sans-serif" fill="#2b2a27">
    <text x="70" y="86" font-size="17">사주 인생 시뮬레이션 · 완료 기록</text>
    ${parts.join('\n    ')}
    <line x1="70" x2="730" y1="${bottom + 12}" y2="${bottom + 12}" stroke="#e6e3dc"/>
    <text x="70" y="${bottom + 49}" font-size="13" fill="#8a867d">${xml(card.limits.join(' '))}</text></g></svg>`;
}

function saveSvg(svg, name) {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = name;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// 나이순 인생 연표: 무슨 일이 · 그해 사주의 이유 · 내가 고른 것
function LifeTimeline({ timeline }) {
  const [all, setAll] = useState(false);
  const lifeRows = timeline.filter((row) => row.kind === 'life').length;
  const shown = all ? timeline : timeline.filter((row) => row.kind !== 'luck' && !row.minor);
  return <details className="panel result-timeline" aria-label="인생 연표" open>
    <summary><b>인생 연표</b> <small className="muted">큰 사건 {lifeRows}개</small></summary>
    <p className="muted small">일어나는 빈도는 통계에 맞췄고, 어느 해에 일어나는지는 사주의 운이 정했고, 결과는 내가 고른 것입니다.{lifeRows === 0 ? ' 이번 기록에는 큰 인생 사건이 없습니다.' : ''}</p>
    <label className="inline"><input type="checkbox" checked={all} onChange={(event) => setAll(event.target.checked)} />대운 전환과 거절한 제안도 보기</label>
    <ol className="timeline-list">
      {shown.map((row, index) => <li key={`${row.age}-${row.kind}-${index}`} className={`timeline-row timeline-${row.kind}${row.minor ? ' timeline-minor' : ''}`}>
        <b className="timeline-age">{row.age}세</b>
        <div>
          <span className="timeline-title">{row.title}{row.grade ? <small className={`grade grade-${row.grade === '게임 추정' ? 'game' : 'stat'}`}>{row.grade}</small> : null}</span>
          {row.why?.length > 0 && <p className="muted small">이유 · {row.why.join(' · ')}</p>}
          {row.chosen && <p className="muted small">선택 · {row.chosen}</p>}
        </div>
      </li>)}
    </ol>
  </details>;
}

function LifeBasis() {
  return <details className="panel result-basis">
    <summary>근거와 한계</summary>
    <ul className="basis-list">{lifeBasis().map((item) => <li key={item.name}><b>{item.name}</b> <small className={`grade grade-${item.grade === '게임 추정' ? 'game' : 'stat'}`}>{item.grade}</small><p className="muted small">{item.note}</p></li>)}</ul>
    <p className="muted small">사주는 사건이 일어나는 시기와 영역의 쏠림만 바꾸고, 평생 일어나는 횟수는 통계에 맞춰 두었습니다. 금액은 2025년 통계에 그해 물가를 반영한 값입니다.</p>
  </details>;
}

export default function ResultPage({ run, onReset, onReplay, lives = [], storageError = '', storageNotice = '' }) {
  const sum = useMemo(() => summarize(run), [run]);
  const card = useMemo(() => buildResultCard(run), [run]);
  const narrative = useMemo(() => buildNarrative(run), [run]);
  const [cardError, setCardError] = useState('');
  const [selectedPrev, setSelectedPrev] = useState('');
  const previous = useMemo(() => pickPrevious(run, lives, selectedPrev), [run, lives, selectedPrev]);
  const timeline = useMemo(() => buildLifeTimeline(run), [run]);
  const flow = useMemo(() => buildMoneyFlow(run, timeline), [run, timeline]);
  const tiles = useMemo(() => buildKeyTiles(run), [run]);
  const d = run.saju.day, m = run.saju.month;
  const controlledReplay = run.engineRulesVersion === LIFE_RULES.version && !!run.referenceData?.fingerprint
    && (run.history ?? []).every((year) => (year.engineRulesVersion ?? year.balanceVersion) === LIFE_RULES.version);
  return <div className="result">
    <header className="rhead">
      <h1>{sum.endAge}세 · 완료한 인생</h1>
      <span className="muted">0~{sum.endAge}세 · {STEM_KO[d.stem]}{BRANCH_KO[d.branch]}일{m ? ` · ${STEM_KO[m.stem]}${BRANCH_KO[m.branch]}월` : ''}</span>
      {run.job && <span className="chip">{E.jobTitle(run.job)} <small>({run.job.name})</small></span>}
      <div className="grow" />
      {onReplay && <button className="primary" onClick={onReplay}>같은 조건으로 다시 살기</button>}
      <button onClick={onReset}>새 출생 입력</button>
    </header>
    <p className="muted result-replay-note">다시 시작하면 이번 기록을 보관하고 같은 출생 입력·원국·시드로 새 인생을 시작합니다. 이번엔 자동 결정을 끄고 직접 골라 보면 기질대로 살았을 때와 비교할 수 있습니다. {controlledReplay ? '사건과 직업 기준 데이터도 보존합니다.' : '이 기록은 이전 규칙 또는 기준 데이터가 없는 기록이므로 완전히 같은 규칙으로 비교할 수 없습니다.'}</p>
    {storageError && <p className="error" role="alert">{storageError}</p>}
    {storageNotice && <p className="muted result-replay-note" role="status">{storageNotice}</p>}

    <section className="result-group result-current" aria-label="이번 선택">
      <WindowTitle>이번 선택</WindowTitle>
      <div className="result-group-body">
    <section className="panel result-card" aria-label="인생 한 장 카드">
      <h2 className="result-section-heading">인생 기록</h2>
      <div className="result-card-content">
      <p className="result-card-eyebrow">{card.endAge}세 · 완료한 인생</p>
      <h3 className="result-card-title">{card.title}</h3>
      {card.dayLabel && <p className="result-daylabel">{card.dayLabel}</p>}
      <div className="result-tiles">{tiles.map((tile) => <div className={`result-tile result-tile-${tile.key}`} key={tile.key}><small>{tile.label}</small><b>{tile.value}</b>{tile.sub && <span>{tile.sub}</span>}</div>)}</div>
      <h3 className="result-numbers-title">능력치 처음 → 마지막</h3>
      <ul className="result-statline">{sum.fields.map((f) => <li key={f.group}><span>{GROUP_LABEL[f.group]}</span><b>{value(f.start)} → {value(f.end)}</b><em className={f.change > 0 ? 'up' : f.change < 0 ? 'down' : ''}>{sum.fmt(f.change)}</em></li>)}</ul>
      <div className="result-numbers">
        {card.numbers.map((number) => <div className="result-number" key={number.group}><b>{number.value}</b><span>{number.label}</span></div>)}
      </div>
      <ol className="result-turnings">
        {TURNING_LABEL.map((label, index) => {
          const point = card.turningPoints[index];
          return point
            ? <li className={`result-turning result-turning-${point.kind}`} key={label}><small>{label}{point.detail ? ` · ${point.detail}` : ''}</small><span>{point.age}세 · {point.title}</span></li>
            : <li className="result-turning result-turning-empty" key={label}><small>{label}</small><span>기록 없음</span></li>;
        })}
      </ol>
      <p className="muted small result-card-limit">{card.limits.join(' ')}</p>
      <div className="row"><button onClick={() => { try { saveSvg(shareSvg(run, card, false, narrative), `인생기록-${card.endAge}세.svg`); setCardError(''); } catch { setCardError('카드를 저장하지 못했습니다. 다시 시도해 주세요.'); } }}>카드 저장 · SVG</button></div>
      {cardError && <p className="error" role="alert">{cardError}</p>}
      </div>
    </section>

    <LifeTimeline timeline={timeline} />
    <MoneyGraph flow={flow} />
      </div>
    </section>
    <section className="result-group result-previous" aria-label="이전 선택과 비교">
      <WindowTitle tone="lavender">이전 선택과 비교</WindowTitle>
      <div className="result-group-body">
    <LifeDrama run={run} lives={lives} previous={previous} onSelect={setSelectedPrev} />
      </div>
    </section>
    <LifeBasis />
  </div>;
}
